import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { useCanOrganizationWide } from "@/lib/policy";
import { toJobAppSections } from "./-components/job-app-sections-editor";
import { JobAppTemplateForm, jobAppTemplateToInput } from "./-components/job-app-template-form";
import { JobAppTemplateList, type JobAppTemplateRow } from "./-components/job-app-template-list";

export const Route = createFileRoute("/_authenticated/_workforce/hiring/job-app-templates")({
  component: JobAppTemplatesPage,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** The full application form, filled in from the applicant's portal once they have passed
 * screening. One per posting, and never graded. */
function JobAppTemplatesPage() {
  const canManage = useCanOrganizationWide("hiring:manage");
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<JobAppTemplateRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<JobAppTemplateRow | null>(null);

  const templates = useQuery(
    orpc.workforce.hiringTemplates.jobApplications.list.queryOptions({ input: {} }),
  );
  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: orpc.workforce.hiringTemplates.jobApplications.key(),
    });

  const create = useMutation(
    orpc.workforce.hiringTemplates.jobApplications.create.mutationOptions({
      onSuccess: async () => {
        toast.success("Template created");
        setCreating(false);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const update = useMutation(
    orpc.workforce.hiringTemplates.jobApplications.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Template updated");
        setEditing(null);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const remove = useMutation(
    orpc.workforce.hiringTemplates.jobApplications.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Template deleted");
        setDeleteTarget(null);
        await invalidate();
      },
      onError: fail,
    }),
  );

  const rows = templates.data ?? [];
  const active = rows.filter((row) => row.isActive).length;

  return (
    <>
      <PageHeader
        title="Application forms"
        meta={
          rows.length > 0
            ? `${rows.length} defined, ${active} active. One per posting, filled in from the applicant's portal once they pass screening. Never graded.`
            : "The full application, filled in after screening is passed."
        }
        actions={
          canManage ? <Button onClick={() => setCreating(true)}>New application form</Button> : null
        }
      />

      {templates.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : templates.isError ? (
        <ReadFailed
          message="Could not load application forms."
          error={templates.error}
          onRetry={() => void templates.refetch()}
          retrying={templates.isFetching}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No application forms yet"
          description="Create templates with sections and typed fields to collect structured information from applicants."
        />
      ) : (
        <JobAppTemplateList
          templates={rows}
          canManage={canManage}
          onEdit={setEditing}
          onDelete={setDeleteTarget}
        />
      )}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>New job application template</DialogTitle>
            <DialogDescription>
              Build sections and fields for the form applicants fill out after passing screening.
            </DialogDescription>
          </DialogHeader>
          <JobAppTemplateForm
            pending={create.isPending}
            submitLabel="Create template"
            onCancel={() => setCreating(false)}
            onSubmit={(values) => create.mutate(jobAppTemplateToInput(values))}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Edit job application template</DialogTitle>
            <DialogDescription>
              Build sections and fields for the form applicants fill out after passing screening.
            </DialogDescription>
          </DialogHeader>
          {editing ? (
            <JobAppTemplateForm
              pending={update.isPending}
              submitLabel="Save changes"
              defaultValues={{
                name: editing.name,
                description: editing.description ?? "",
                isActive: editing.isActive,
                sections: toJobAppSections(editing.sections),
              }}
              onCancel={() => setEditing(null)}
              onSubmit={(values) =>
                update.mutate({
                  jobAppTemplateId: editing.id,
                  ...jobAppTemplateToInput(values),
                })
              }
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this template?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{deleteTarget?.name}&rdquo; will be permanently deleted. Any job postings
              linked to this template lose the association; submissions already made are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => deleteTarget && remove.mutate({ jobAppTemplateId: deleteTarget.id })}
            >
              {remove.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
